import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Eye } from "lucide-react";
import { apiClient } from "../../config";

const Inquiries = () => {
  const PAGE_SIZE = 10;
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [currentPage, setCurrentPage] = useState(1);

  const statusOptions = ["All", "New", "Contacted", "Qualified", "Lost"];

  useEffect(() => {
    const mapUniversityToInquiry = (u) => ({
      _id: u._id,
      institutionName: u.university_name || "-",
      institutionType: u.institution_type || "-",
      websiteUrl: u.university_website || "",
      country: u.country || "-",
      state: u.state || "-",
      contactPersonName: u.contact_person_name || "-",
      officialEmail: u.university_email || "-",
      contactNumber: u.primary_contact || "-",
      status: u.status || "New",
      priority: u.priority || "Medium",
      createdAt: u.createdAt || new Date().toISOString(),
      updatedAt: u.updatedAt || new Date().toISOString(),
      followUps: Array.isArray(u.followUps) ? u.followUps : [],
    });

    const fetchInquiries = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await apiClient.get("/api/auth/universities");
        const payload = response.data;
        const universities = Array.isArray(payload?.universities) ? payload.universities : [];
        setInquiries(universities.map(mapUniversityToInquiry));
      } catch (err) {
        setError(err.response?.data?.message || err.message || "Failed to fetch inquiries");
        setInquiries([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInquiries();
  }, []);

  const filteredInquiries = useMemo(() => {
    return inquiries
      .filter((item) => {
        const q = searchTerm.toLowerCase();
        return (
          item.institutionName.toLowerCase().includes(q) ||
          item.contactPersonName.toLowerCase().includes(q) ||
          item.officialEmail.toLowerCase().includes(q) ||
          item.contactNumber.includes(searchTerm)
        );
      })
      .filter((item) => {
        if (statusFilter === "All") return true;
        return item.status === statusFilter;
      })
      .filter((item) => {
        const { startDate, endDate } = dateRange;
        if (!startDate || !endDate) return true;
        const created = new Date(item.createdAt);
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        return created >= start && created <= end;
      });
  }, [inquiries, searchTerm, statusFilter, dateRange]);

  const totalPages = Math.max(1, Math.ceil(filteredInquiries.length / PAGE_SIZE));
  const paginatedInquiries = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    // We reverse the filtered list to show newest inquiries first
    return [...filteredInquiries].reverse().slice(start, start + PAGE_SIZE);
  }, [filteredInquiries, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleDateChange = (e) => {
    setDateRange((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const getStatusBadge = (status) => {
    if (status === "New") return "success";
    if (status === "Qualified") return "info";
    if (status === "Lost") return "danger";
    return "warning";
  };

  const getPriorityBadge = (priority) => {
    if (priority === "High") return "danger";
    if (priority === "Medium") return "warning";
    return "info";
  };

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i += 1) pages.push(i);
      return pages;
    }
    pages.push(1);
    if (currentPage > 3) pages.push("...");
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i += 1) pages.push(i);
    if (currentPage < totalPages - 2) pages.push("...");
    pages.push(totalPages);
    return pages;
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-semibold mb-0">All Inquiries</h4>
      </div>

        {/* Filters */}
        <div className="card mb-4 border-0">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <label htmlFor="inqSearch" className="form-label fw-semibold">Search</label>
                <input
                  id="inqSearch"
                  type="text"
                  className="form-control"
                  placeholder="Institution, contact, email, phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label htmlFor="inqStatus" className="form-label fw-semibold">Status</label>
                <select
                  id="inqStatus"
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {statusOptions.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label htmlFor="inqStartDate" className="form-label fw-semibold">Start Date</label>
                <input
                  id="inqStartDate"
                  name="startDate"
                  type="date"
                  className="form-control"
                  value={dateRange.startDate}
                  onChange={handleDateChange}
                />
              </div>
              <div className="col-md-3">
                <label htmlFor="inqEndDate" className="form-label fw-semibold">End Date</label>
                <input
                  id="inqEndDate"
                  name="endDate"
                  type="date"
                  className="form-control"
                  value={dateRange.endDate}
                  onChange={handleDateChange}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table Card */}
        <div className="card border-0">
          <div className="card-body p-0">
            {loading && (
              <div className="p-4 text-center text-muted">
                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                Loading inquiries...
              </div>
            )}
            {!loading && error && <div className="alert alert-danger m-4">{error}</div>}

            {!loading && (
              <div className="table-responsive">
                <table className="table table-striped table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th className="ps-4" style={{ width: "60px" }}>#</th>
                      <th>Institution</th>
                      <th>Contact</th>
                      <th>Country / State</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Created On</th>
                      <th className="pe-4 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedInquiries.length > 0 ? (
                      paginatedInquiries.map((item, idx) => (
                        <tr key={item._id}>
                          <td className="ps-4">{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                          <td className="fw-semibold">
                            <div>{item.institutionName}</div>
                            <small className="text-muted" style={{ fontSize: '0.8rem' }}>{item.institutionType}</small>
                          </td>
                          <td>
                            <div>{item.contactPersonName}</div>
                            <small className="text-muted" style={{ fontSize: '0.8rem' }}>{item.officialEmail}</small>
                          </td>
                          <td>{item.country}, {item.state}</td>
                          <td><span className={`badge bg-${getStatusBadge(item.status)}`}>{item.status}</span></td>
                          <td><span className={`badge bg-${getPriorityBadge(item.priority)}`}>{item.priority}</span></td>
                          <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                          <td className="pe-4 text-end">
                            <Link to={`/inquiry/${item._id}`} className="btn btn-sm btn-primary">
                              <div className="d-flex align-items-center gap-1">
                                <Eye size={14} />
                                <span>View</span>
                              </div>
                            </Link>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center py-5 text-muted">No inquiries found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {filteredInquiries.length > 0 && !loading && (
              <div className="d-flex align-items-center justify-content-between p-4 border-top">
                <small className="text-muted">
                  Showing {(currentPage - 1) * PAGE_SIZE + 1} to {Math.min(currentPage * PAGE_SIZE, filteredInquiries.length)} of {filteredInquiries.length}
                </small>
                <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    style={{ minWidth: '88px' }}
                  >
                    Previous
                  </button>
                  {getPageNumbers().map((page, idx) => (
                    page === "..." ? (
                      <span key={`dots-${idx}`} className="text-muted px-1">...</span>
                    ) : (
                      <button
                        key={`page-${page}`}
                        type="button"
                        className={`btn btn-sm ${currentPage === page ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setCurrentPage(page)}
                        style={{ minWidth: '34px' }}
                      >
                        {page}
                      </button>
                    )
                  ))}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    style={{ minWidth: '70px' }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </>
  );
};

export default Inquiries;
